import React from 'react';
import { Download, Bell, Hand, MapPin, Users, Home, School, Building2, Lock, Compass, AlertCircle, Radio, HeartHandshake, Sparkles } from 'lucide-react';

export const HowItWorks = () => {
  const steps = [
    {
      step: 1,
      title: "Download. Sync. Discover.",
      subtitle: "Download the app, sync your contacts, and stay visible.",
      description: "Fuzz instantly shows you familiar faces nearby — and gently alerts you when friends cross your path.",
      features: [
        { icon: Bell, text: "Get proximity alerts" },
        { icon: Hand, text: "Wave anytime with a Fuzz" },
        { icon: MapPin, text: "Feel connected without texting first" }
      ],
      tagline: "It's like real life… but smarter.",
      color: "teal",
      gradient: "from-teal-500 to-emerald-500"
    },
    {
      step: 2,
      title: "Build your Vibe. Stay in sync.",
      subtitle: "Your Vibe is your private circle.",
      description: "Add close friends and family, mark important safe zones, and stay updated — without being intrusive.",
      features: [
        { icon: Home, text: "Home • School • Office safe zones" },
        { icon: Lock, text: "Only trusted people" },
        { icon: Compass, text: "Know when loved ones are in/out of safe areas" }
      ],
      tagline: "Your people, always within reach.",
      color: "emerald",
      gradient: "from-emerald-500 to-green-500"
    },
    {
      step: 3,
      title: "SOS when you need it most.",
      subtitle: "One tap when it matters most.",
      description: "Activate SOS to instantly alert your trusted emergency circle and share your live location in real time.",
      features: [
        { icon: AlertCircle, text: "Immediate emergency signal" },
        { icon: Radio, text: "Live location sent instantly" },
        { icon: HeartHandshake, text: "Help reaches you faster" }
      ],
      tagline: "Because safety should never be complicated.",
      color: "rose",
      gradient: "from-rose-500 to-orange-500"
    }
  ];

  return (
    <section id="how-it-works" className="py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">
            How It Works
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 max-w-2xl mx-auto">
            Real-world connection, made effortless.
          </p>
        </div>

        {/* Steps */}
        <div className="max-w-5xl mx-auto space-y-16">
          {steps.map((step, index) => (
            <div 
              key={index} 
              className={`relative ${index % 2 === 0 ? '' : 'md:flex-row-reverse'}`}
            >
              {/* Card */}
              <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden hover:shadow-2xl transition-all">
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Left side - Content */}
                  <div className="p-8 md:p-12 flex flex-col justify-center">
                    {/* Step badge */}
                    <div className="inline-flex items-center gap-3 mb-6">
                      <div className={`w-12 h-12 bg-gradient-to-br ${step.gradient} text-white rounded-full flex items-center justify-center font-bold text-lg shadow-lg`}>
                        {step.step}
                      </div>
                      <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                        Step {step.step}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                      {step.title}
                    </h3>

                    {/* Subtitle */}
                    <p className="text-lg font-medium text-gray-600 mb-4">
                      {step.subtitle}
                    </p>

                    {/* Description */}
                    <p className="text-gray-600 leading-relaxed mb-6">
                      {step.description}
                    </p>

                    {/* Features list */}
                    <div className="space-y-3 mb-6">
                      {step.features.map((feature, idx) => {
                        const Icon = feature.icon;
                        return (
                          <div key={idx} className="flex items-start gap-3">
                            <div className={`w-8 h-8 bg-gradient-to-br ${step.gradient} rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5`}>
                              <Icon className="h-4 w-4 text-white" />
                            </div>
                            <span className="text-gray-700 leading-relaxed">{feature.text}</span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Tagline */}
                    <p className={`text-lg font-semibold bg-gradient-to-r ${step.gradient} bg-clip-text text-transparent`}>
                      {step.tagline}
                    </p>
                  </div>

                  {/* Right side - Visual */}
                  <div className={`relative bg-gradient-to-br ${step.gradient} p-12 flex items-center justify-center`}>
                    {/* Decorative elements */}
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
                      <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
                    </div>
                    
                    {/* Large icon */}
                    <div className="relative z-10">
                      {index === 0 && <Download className="h-32 w-32 text-white opacity-80" />}
                      {index === 1 && <Users className="h-32 w-32 text-white opacity-80" />}
                      {index === 2 && <AlertCircle className="h-32 w-32 text-white opacity-80" />}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Closing statement */}
        <div className="mt-20 text-center">
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-teal-500 via-emerald-500 to-green-500 text-white px-8 py-4 rounded-full shadow-lg">
            <Sparkles className="h-5 w-5" />
            <p className="text-lg font-semibold">
              Fuzz brings closeness, comfort, and safety into one simple map.
            </p>
            <Sparkles className="h-5 w-5" />
          </div>
        </div>
      </div>
    </section>
  );
};
